import React from 'react';
import {
  ServiceHelpPage,
  STANDARD_HELP_TABS,
  Card,
  FeatureCard,
  Step,
  FaqItem,
  Issue,
  WelcomeHero,
  QuickStartPanel,
  SectionPanel,
  AnalyticsLinkButton,
} from '@saas-foundation/shared-frontend/help';
import {
  MessageSquare,
  BarChart3,
  Zap,
  Shield,
  Bot,
  Sparkles,
  Play,
  CheckCircle,
  Settings,
  FileText,
  Brain,
  Target,
  TrendingUp,
  HelpCircle,
  Activity,
  Users,
} from 'lucide-react';

const tabs = STANDARD_HELP_TABS.map((tab) =>
  tab.id === 'analytics' ? { ...tab, label: 'Analytics & Storylines' } : tab
);

const Help = () => {
  const renderGettingStarted = () => (
    <div className="space-y-6">
      <WelcomeHero
        icon={<Bot className="w-6 h-6 text-blue-600" />}
        title="Welcome to Agentic All‑in‑One RCM"
        description="AgenticRCM is the unified shell that helps you navigate across Denials, Claims, Charges, ERA, Eligibility, Prior Auth, AR Posting, and Contract Management—without switching apps."
        cards={[
          {
            title: 'Dashboard',
            icon: <Activity className="w-5 h-5 text-blue-600" />,
            description: 'A single landing page for cross‑service visibility and navigation.',
          },
          {
            title: 'Work Queue',
            icon: <Users className="w-5 h-5 text-indigo-600" />,
            description: 'Consolidated operational work items and follow‑ups across services.',
          },
          {
            title: 'Analytics',
            icon: <BarChart3 className="w-5 h-5 text-green-600" />,
            description: 'Navigate into each service’s analytics and tell end‑to‑end storylines.',
          },
        ]}
      />
      <QuickStartPanel title="Quick Start Guide" icon={Play}>
        <Step number={1} title="Pick the workflow you need">
          Use the sidebar to open the service you care about (Denials, Claims, Charges, ERA, Eligibility, Prior Auth, AR Posting).
        </Step>
        <Step number={2} title="Use service analytics for deep dives">
          Once inside a service, open its <span className="font-medium">Analytics</span> page for detailed insights.
        </Step>
        <Step number={3} title="Use the shell for end-to-end storylines">
          Walk workflows like “eligibility → prior auth → claim → ERA → denial/underpayment” without leaving the app.
        </Step>
        <Step number={4} title="Use AI for summaries and routing">
          Use Copilot to summarize trends, recommend next actions, and help triage priorities across services.
        </Step>
      </QuickStartPanel>
    </div>
  );

  const renderWorkflows = () => (
    <div className="space-y-6">
      <SectionPanel title="Core Shell Workflows" icon={FileText}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card title="1. Navigate to service workflows" icon={<FileText className="w-5 h-5 text-blue-600" />}>
            Use the sidebar to open the service for the task at hand:
            <ul className="mt-2 list-disc list-inside text-sm text-gray-600 space-y-1">
              <li>Denials → denial worklists and appeals.</li>
              <li>Claims → claim submission quality and lifecycle.</li>
              <li>Charges → capture, coding, CDI, queue review.</li>
              <li>ERA → payment behavior, CARC/RARC, payer drift.</li>
            </ul>
          </Card>
          <Card title="2. Use Work Queue for prioritization" icon={<Users className="w-5 h-5 text-indigo-600" />}>
            Use Work Queue to keep focus and manage follow‑ups:
            <ul className="mt-2 list-disc list-inside text-sm text-gray-600 space-y-1">
              <li>Prioritize urgent items and SLA risk.</li>
              <li>Track tasks and notifications from multiple services.</li>
              <li>Drill through into the owning service for execution.</li>
            </ul>
          </Card>
          <Card title="3. Reporting and cross-service summaries" icon={<Target className="w-5 h-5 text-purple-600" />}>
            Use Reporting to share outcomes:
            <ul className="mt-2 list-disc list-inside text-sm text-gray-600 space-y-1">
              <li>Export or share snapshots for leadership updates.</li>
              <li>Align service metrics into a single narrative.</li>
            </ul>
          </Card>
          <Card title="4. Agent Studio integration" icon={<CheckCircle className="w-5 h-5 text-red-500" />}>
            Use Agent Studio (when enabled) to build and run agents that call service APIs and summarize analytics.
          </Card>
        </div>
      </SectionPanel>
    </div>
  );

  const renderAIAssistant = () => (
    <div className="space-y-6">
      <SectionPanel title="Using the AI Copilot & Kai - AI Coworker" icon={MessageSquare}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card title="AI Copilot (chat)" icon={<Bot className="w-5 h-5 text-blue-600" />}>
            Ask for cross‑service summaries and guidance:
            <ul className="mt-2 list-disc list-inside text-sm text-gray-600 space-y-1">
              <li>“Summarize denial and ERA trends this month.”</li>
              <li>“Which payers are drifting and why?”</li>
              <li>“What are the biggest revenue risks this week?”</li>
            </ul>
          </Card>
          <Card title="Kai - AI Coworker (routing assistant)" icon={<Sparkles className="w-5 h-5 text-indigo-600" />}>
            Use Kai to route and prioritize:
            <ul className="mt-2 list-disc list-inside text-sm text-gray-600 space-y-1">
              <li>Suggest which service to open for a given issue.</li>
              <li>Recommend drill‑through targets from high-level KPIs.</li>
              <li>Support a consistent “BI vs AI” narrative across services.</li>
            </ul>
          </Card>
        </div>
      </SectionPanel>
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <SectionPanel title="Analytics & Storylines" icon={BarChart3}>
        <div className="space-y-4">
          <Card title="Service-owned analytics" icon={<TrendingUp className="w-5 h-5 text-blue-600" />}>
            Analytics are owned by each service (Denials, Claims, Charges, ERA, Eligibility, Prior Auth, AR Posting).
            The shell embeds those views so you can navigate smoothly.
          </Card>
          <Card title="End-to-end storylines" icon={<Target className="w-5 h-5 text-purple-600" />}>
            Use the shell to tell connected stories: eligibility and prior auth upstream → claims submission → ERA outcomes →
            denials and underpayments → contract validation.
          </Card>
          <Card title="AI contribution and impact" icon={<Brain className="w-5 h-5 text-emerald-600" />}>
            Many services include “AI contribution” metrics. Use them consistently to explain where AI is assisting vs autonomous.
          </Card>
        </div>
      </SectionPanel>
    </div>
  );

  const renderAnalyticsFAQ = () => (
    <div className="space-y-6">
      <SectionPanel title="Analytics FAQ (AgenticRCM Shell)" icon={BarChart3}>
        <div className="space-y-4">
          <FaqItem question="Does AgenticRCM have its own analytics?">
            AgenticRCM embeds analytics from each RCM service; it does not redefine those service dashboards.
            Use the sidebar to open a service and then use its Analytics page.
          </FaqItem>
          <FaqItem question="Where do I start if I want “overall RCM analytics”?">
            Start with the shell Dashboard and Work Queue, then drill into the owning service’s Analytics for details.
          </FaqItem>
        </div>
        <AnalyticsLinkButton href="/analytics" label="Go to RCM Analytics" />
      </SectionPanel>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <SectionPanel title="Settings & Demo Mode" icon={Settings}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card title="Organization selection" icon={<Sparkles className="w-5 h-5 text-amber-500" />}>
            Ensure you’re working in the correct organization; service frames use this context for analytics and operations.
          </Card>
          <Card title="AI / Feature flags" icon={<Activity className="w-5 h-5 text-blue-600" />}>
            Configure AI and features (when available) so the shell and services behave consistently during demos.
          </Card>
        </div>
      </SectionPanel>
    </div>
  );

  const renderTroubleshooting = () => (
    <div className="space-y-6">
      <SectionPanel title="Common Issues & Solutions" icon={Shield}>
        <div className="space-y-4">
          <Issue title="Embedded service page looks blank" color="border-amber-500">
            Confirm the underlying service is running and reachable. Service analytics are rendered from the service itself.
          </Issue>
          <Issue title="Analytics shows no data" color="border-blue-500">
            Verify you selected the correct organization and date range in the underlying service’s analytics page.
          </Issue>
          <Issue title="Navigation mismatch" color="border-green-500">
            Refresh the page and reselect the service from the sidebar to re-sync route and embedded frame state.
          </Issue>
        </div>
      </SectionPanel>
    </div>
  );

  const renderFeatures = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FeatureCard
          icon={<MessageSquare className="w-5 h-5 text-blue-600" />}
          title="AICoworker(Kai)"
          description="Cross-service copiloting: ask questions, get summaries, and route work across claims, denials, ERA, and more."
        />
        <FeatureCard
          icon={<BarChart3 className="w-5 h-5 text-green-600" />}
          iconBg="bg-green-100"
          title="Storyline analytics"
          description="Connect KPIs across services (837 → 835 → AR → denials) to tell the end-to-end RCM performance story."
        />
        <FeatureCard
          icon={<Zap className="w-5 h-5 text-blue-600" />}
          title="Unified navigation"
          description="Jump into each service’s key pages (queues, dashboards, analytics) without switching apps."
        />
        <FeatureCard
          icon={<Shield className="w-5 h-5 text-orange-600" />}
          iconBg="bg-orange-100"
          title="Consistent operating model"
          description="Standard tabs, workflows, and artifacts across services so teams can onboard faster and execute consistently."
        />
      </div>
    </div>
  );

  const renderFAQ = () => (
    <div className="space-y-6">
      <SectionPanel title="Frequently Asked Questions" icon={HelpCircle}>
        <div className="space-y-4">
          <FaqItem question="What is AgenticRCM vs individual services?">
            AgenticRCM is the shell that unifies navigation, workflows, and storylines across RCM services.
            Execution still happens inside each specialized service.
          </FaqItem>
          <FaqItem question="Where can I learn Analytics?">
            Use the <span className="font-medium">Analytics FAQ</span> tab for analytics-specific questions.
            This FAQ focuses on navigation and operating model.
          </FaqItem>
        </div>
      </SectionPanel>
    </div>
  );

  return (
    <ServiceHelpPage
      title="Help & Documentation"
      subtitle="Learn how to use AgenticRCM to navigate service workflows, analytics, and AI storylines."
      tabs={tabs}
      renderers={{
        'getting-started': renderGettingStarted,
        workflows: renderWorkflows,
        'ai-assistant': renderAIAssistant,
        analytics: renderAnalytics,
        'analytics-faq': renderAnalyticsFAQ,
        features: renderFeatures,
        faq: renderFAQ,
        settings: renderSettings,
        troubleshooting: renderTroubleshooting,
      }}
    />
  );
};

export default Help;
